import React, { useEffect, useState } from 'react'
import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import set from 'lodash/set'

import Button from '@react/react-spectrum/Button'
import Dialog from '@react/react-spectrum/Dialog'
import ModalTrigger from '@react/react-spectrum/ModalTrigger'
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger'
import { Table, TBody, TD, TH, THead, TR } from '@react/react-spectrum/Table'
import NumberInput from '@react/react-spectrum/NumberInput'
import Textfield from '@react/react-spectrum/Textfield'
import Tooltip from '@react/react-spectrum/Tooltip'

import Delete from '@react/react-spectrum/Icon/Delete'
import Edit from '@react/react-spectrum/Icon/Edit'
import Help from '@react/react-spectrum/Icon/Help'

import './ProcessEditor.scss'

const DEFAULT_COLUMNS = [
  { key: 'eventType', label: 'Event type' },
  { key: 'step', label: 'Name' },
  { key: 'description', label: 'Description' },
]

export const ProcessEditor = ({ fields, disabled, onChange, columns, emptyRow: emptyRowProp }) => {
  const cols = columns && columns.length ? columns : DEFAULT_COLUMNS
  const [rows, setRows] = useState([])
  const hasEmptyRow = rows.length > 0 && rows.filter((r) => !r.eventType).length > 0
  const hasContentStateColumns = cols.some((c) => String(c.key).startsWith('contentState.'))

  const saveField = (index, propName, column) => (value) => {
    let _value = propName === 'name' ? value.replace(/(^[0-9]+|-|\s)/gm, '') : value
    if (column?.type === 'number') {
      _value = value === '' || value === null || value === undefined ? undefined : Number(value)
      if (Number.isNaN(_value)) _value = undefined
    }
    const newRows = rows.map((r, idx) => {
      if (idx !== index) return r
      if (String(propName).includes('.')) {
        const next = cloneDeep(r)
        set(next, propName, _value)
        return next
      }
      return { ...r, [propName]: _value }
    })
    setRows(newRows)
    onChange(newRows)
  }

  const addRow = () => {
    if (!hasEmptyRow) {
      let defaultRow
      if (emptyRowProp != null && typeof emptyRowProp === 'object') {
        defaultRow = cloneDeep(emptyRowProp)
      } else if (hasContentStateColumns) {
        defaultRow = { contentState: {} }
      } else {
        defaultRow = {}
      }
      const newRows = [...rows, defaultRow]
      setRows(newRows)
      onChange(newRows)
    }
  }

  const deleteRow = (index) => {
    const newRows = rows.filter((r, idx) => idx !== index)
    setRows(newRows)
    onChange(newRows)
  }

  useEffect(() => {
    setRows(fields || [])
  }, [fields])

  return (
    <div className='processEditor'>
      <Table onSubmit={(evt) => evt.preventDefault()}>
        <THead>
          {cols.map((c) => (
            <TH key={c.key}>{c.label}</TH>
          ))}
          <TH />
        </THead>
        <TBody>
          {rows.map((row, idx) => (
            <TR key={'field-' + idx}>
              {cols.map((c) => (
                <TD key={c.key}>
                  {c.type === 'number' ? (
                    <NumberInput
                      value={get(row, c.key)}
                      min={c.min}
                      max={c.max}
                      step={c.step ?? 1}
                      onChange={saveField(idx, c.key, c)}
                      disabled={disabled}
                    />
                  ) : (
                    <Textfield value={get(row, c.key) ?? ''} onChange={saveField(idx, c.key, c)} disabled={disabled} />
                  )}
                </TD>
              ))}
              <TD>
                <Button
                  variant='tool'
                  aria-label='Delete'
                  icon={<Delete />}
                  onClick={() => deleteRow(idx)}
                  disabled={disabled}
                />
              </TD>
            </TR>
          ))}
          <TR onClick={addRow}>
            <TD>
              <Button variant='action' quiet label='+ Add' disabled={disabled || hasEmptyRow} />
            </TD>
            {cols.slice(1).map((c) => (
              <TD key={'add-' + c.key} />
            ))}
            <TD />
          </TR>
        </TBody>
      </Table>
    </div>
  )
}

export const ProcessEditorDialog = ({ label, fields, onChange, columns, emptyRow, ...otherProps }) => {
  const [newContent, setNewContent] = useState(fields || [])
  return (
    <Dialog
      {...otherProps}
      className={'process-editor--dialog'}
      title={label || 'Process Editor'}
      confirmLabel='Save'
      cancelLabel='Discard'
      onConfirm={() => onChange(newContent)}
      role='dialog'
    >
      <ProcessEditor fields={newContent} onChange={setNewContent} columns={columns} emptyRow={emptyRow} />
    </Dialog>
  )
}

export const ProcessEditorInput = ({ label, fields, disabled, onChange, columns, emptyRow }) => (
  <ModalTrigger>
    <Button icon={<Edit />}>Open Editor</Button>
    <ProcessEditorDialog
      label={label}
      fields={fields}
      onChange={onChange}
      disabled={disabled}
      columns={columns}
      emptyRow={emptyRow}
    />
  </ModalTrigger>
)
